/**
 * Presentation renderer (Library / file-preview).
 *
 * Renders a `kind: "presentation"` artifact through the exact same
 * {@link SlideDeckView} the chat artifact canvas uses — thumbnail sidebar +
 * single-slide stage — so opening a slide deck from the Library looks
 * identical to opening it in chat, instead of the old flat, PDF-like
 * vertical scroll.
 *
 * This path is read-only: it parses the persisted body into slides once and
 * drives the deck in its uncontrolled mode (the view owns slide selection).
 * Streaming/edit state is a chat-canvas concern and never applies here.
 */
import { Loader2 } from "lucide-react";
import { useMemo } from "react";

import {
  SlideDeckView,
  type DisplaySlide,
} from "@/components/artifacts/slide-deck-view";
import { parseSlides } from "@/lib/artifacts/slides";

import { useFileText } from "../hooks/use-file-text";
import type { FileRendererProps } from "../types";

export function PresentationRenderer({ source }: FileRendererProps) {
  const state = useFileText(source);

  /** Parse the persisted body into read-only slides (never streaming here). */
  const slides = useMemo<DisplaySlide[]>(() => {
    if (state.status !== "ready") return [];
    return parseSlides(state.text).map((s) => ({ ...s, streaming: false }));
  }, [state]);

  if (state.status === "loading" || state.status === "idle") {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="size-8 animate-spin text-primary" />
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 text-sm text-muted-foreground">
        <p>Failed to load presentation.</p>
        <p className="text-xs opacity-70">{state.error.message}</p>
      </div>
    );
  }

  if (slides.length === 0) {
    return (
      <div className="h-full flex items-center justify-center text-muted-foreground italic">
        No slides yet
      </div>
    );
  }

  return (
    <SlideDeckView
      slides={slides}
      title={source.filename || "Presentation preview"}
      showSlideCounter
      className="h-full"
    />
  );
}
